import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

// shadcn-style Calendar wrapping react-day-picker (v9). Supports single/range
// selection and multiple months. Range styling: filled start/end, shaded middle.
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fixedWeeks = true,
  ...props
}: CalendarProps): React.ReactElement {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      // Always render 6 week rows so the calendar has a constant height. Months
      // otherwise need 5 or 6 rows (in 2025: Mar/Aug/Nov need 6), which would
      // resize the card — and shift the time fields and footer — on every month
      // change. Overridable per consumer.
      fixedWeeks={fixedWeeks}
      // `relative` anchors the absolutely-positioned nav to the calendar root
      // (react-day-picker's default stylesheet does this; we replicate it since
      // the shadcn setup styles via classNames instead of importing that CSS).
      // `rdp-calendar` is a styling hook for app.css (the month grid is a real
      // <table>, so the global `tr:hover button` underline rule catches day
      // buttons and must be neutralised there — app.css is unlayered, so a
      // Tailwind utility can't win).
      className={cn("rdp-calendar relative p-3", className)}
      classNames={{
        months: "flex flex-col gap-4 sm:flex-row sm:gap-8",
        // 12px vertical rhythm between caption, weekday header and week rows.
        month: "flex flex-col gap-3",
        month_caption: "flex h-8 items-center justify-center",
        caption_label: "text-sm font-semibold",
        // Single nav spanning the row: prev at the far left, next at the far
        // right, centered vertically on the caption row.
        nav: "absolute inset-x-0 top-0 flex h-8 items-center justify-between",
        button_previous: cn(
          buttonVariants({ variant: "ghost" }),
          "size-7 p-0 text-muted-foreground hover:text-foreground"
        ),
        button_next: cn(
          buttonVariants({ variant: "ghost" }),
          "size-7 p-0 text-muted-foreground hover:text-foreground"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "w-10 text-[0.8rem] font-normal text-muted-foreground select-none",
        week: "mt-3 flex w-full",
        day: "relative size-10 p-0 text-center text-sm",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "size-10 rounded-md p-0 text-sm font-normal"
        ),
        // The shaded band lives on the day CELL and is driven by rdp's own range
        // modifiers (`aria-selected` sits on the cell itself, so a `:has()`
        // selector can never match it). The filled pill lives on the button.
        range_start:
          "bg-accent rounded-l-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground",
        range_end:
          "bg-accent rounded-r-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground",
        // Middle days also carry `selected`, whose white-on-primary fill would
        // otherwise make them invisible against the band — override explicitly.
        range_middle:
          "bg-accent [&>button]:bg-transparent! [&>button]:text-foreground! [&>button]:hover:bg-background/60",
        selected:
          "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground",
        today: "[&>button]:font-semibold",
        // Adjacent-month days stay dimmed and never render the filled
        // start/end pill: with two months side by side the same date appears in
        // both panels, and two black pills for one date reads as two selections.
        // The cell keeps its band, so a range still flows across the boundary.
        outside:
          "text-muted-foreground/50 [&>button]:bg-transparent! [&>button]:text-muted-foreground/50!",
        disabled: "text-muted-foreground opacity-50",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: iconClassName }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
          return <Icon className={cn("size-4", iconClassName)} />;
        },
      }}
      {...props}
    />
  );
}

export { Calendar };
